import { useEffect, lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { PersonaProvider } from "@/contexts/PersonaContext";

import { useIsMobile } from "@/hooks/use-mobile";
import { initGA, grantAnalyticsConsent } from "@/lib/gtag";
import { hasAnalyticsConsent } from "@/lib/consent";
import Layout from "@/components/Layout";
import PageSkeleton from "@/components/PageSkeleton";

// Critical route — eagerly loaded
import Index from "./pages/Index.tsx";

// Deferred eager imports
const MobileApp = lazy(() => import("@/components/mobile/MobileApp"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));

// Lazy-loaded routes for smaller initial bundle
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin.tsx"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const CaseStudiesPage = lazy(() => import("./pages/CaseStudiesPage.tsx"));
const CaseStudyDetailPage = lazy(() => import("./pages/CaseStudyDetailPage.tsx"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage.tsx"));
const ContactPage = lazy(() => import("./pages/ContactPage.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Music = lazy(() => import("./pages/Music.tsx"));
const TrialHero = lazy(() => import("./pages/TrialHero.tsx"));
const AboutPage = lazy(() => import("./pages/AboutPage.tsx"));
const AiAgenticPage = lazy(() => import("./pages/AiAgenticPage.tsx"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.tsx"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog.tsx"));
const AdminBlogEdit = lazy(() => import("./pages/admin/AdminBlogEdit.tsx"));
const AdminBlogGenerate = lazy(() => import("./pages/admin/AdminBlogGenerate.tsx"));
const AdminBlogSettings = lazy(() => import("./pages/admin/AdminBlogSettings.tsx"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads.tsx"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics.tsx"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers.tsx"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter.tsx"));

const queryClient = new QueryClient();

function AppContent() {
  const isMobile = useIsMobile();

  // Boot GA with Consent Mode v2 defaults; grant if user already accepted
  useEffect(() => {
    initGA();
    if (hasAnalyticsConsent()) {
      grantAnalyticsConsent();
    }
  }, []);

  if (isMobile) {
    return (
      <Suspense fallback={<PageSkeleton fullscreen />}>
        <MobileApp />
      </Suspense>
    );
  }

  return (
    <>
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
      <Suspense fallback={<PageSkeleton fullscreen />}>
        <Routes>
          {/* Pages with shared Navbar + Footer */}
          <Route element={<Layout />}>
            <Route path="/" element={<Index />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/ai" element={<AiAgenticPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Standalone pages (own layout) */}
          <Route path="/music" element={<Music />} />
          <Route path="/trial-hero" element={<TrialHero />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="subscribers" element={<AdminSubscribers />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="blog/new" element={<AdminBlogEdit />} />
            <Route path="blog/edit/:id" element={<AdminBlogEdit />} />
            <Route path="blog/generate" element={<AdminBlogGenerate />} />
            <Route path="blog/settings" element={<AdminBlogSettings />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <PersonaProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </TooltipProvider>
      </PersonaProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
